import { useEffect, useContext, useMemo } from "react";
import { useImmer } from "use-immer";
import { successMessage, errorMessage } from "../../../utils";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../store/AppScopeProvider";
import { TableStateContext } from "../../../store";
import { usePermission } from "../../../shared";
import { useDebounce } from "use-debounce";
import {
  createSurcharges,
  deleteSurChargesById,
  editSurcharges,
  getSurcharges,
  getSurchargesDetails,
} from "../api";
import { useTax } from "../..";

const initialFilter = {
  channel: [],
  saleType: [],
};

export const useSurcharges = ({ load, SurchargesId, isList }) => {
  const navigate = useNavigate();
  const { appState } = useContext(AppContext);
  const { globalData } = appState;
  const {
    filterState: {
      params: { surcharges: storedSurcharges },
    },
    filterStateDispatch,
  } = useContext(TableStateContext);
  const stored = storedSurcharges?.filterApplied;

  const [state, setState] = useImmer({
    isBusy: false,
    isFormChanged: false,
    initialLoad: true,
    surchargesList: [],
    deleteModalOpen: false,
    selectedID: null,
    name: "",
    data: {
      name: "",
      amount: "",
      sale_type_id: [],
      tax_group_id: "",
      category: "",
      fee_type: "percentage",
      channel_id: [],
      in_store_auto_apply: 0,
      public_holiday_fee_enabled: 0,
      is_it_refundable: false,
    },
    filters: {
      isSearching: false,
      searchText: "",
      isFilterApplied: false,
      filter: stored.filter ? stored.filter : initialFilter,
      appliedFilters: stored.appliedFilters ? stored.appliedFilters : [],
    },
  });
  const { state: taxState } = useTax({ isList });
  const [debouncedText] = useDebounce(state.filters.searchText, 1000);
  const isCreateVisible = usePermission("surcharge-create");
  const isEditable = usePermission("surcharge-modify");
  const isDeletable = usePermission("surcharge-delete");
  useEffect(() => {
    if (load) {
      getSurChargesList();
    }
  }, [load]);
  const getSurChargesList = async (search = null) => {
    try {
      setState((draft) => {
        draft.isBusy = true;
        draft.initialLoad = false;
      });
      const res = await getSurcharges(search);
      if (res.success) {
        setState((draft) => {
          draft.isBusy = false;
          draft.surchargesList = res.data;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };
  const onSaveSurcharges = async () => {
    try {
      setState((draft) => {
        draft.isSaveButtonBusy = true;
      });
      const data = {
        ...state.data,
        sale_type_id: state.data.sale_type_id.map((i) => i.id),
        channel_id: state.data.channel_id.map((i) => i.id),
      };
      const res = await createSurcharges(data);
      if (res.success) {
        successMessage("Created Successfully");
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
        navigate("/surcharges");
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };
  const onUpdateSurcharges = async (id) => {
    try {
      setState((draft) => {
        draft.isSaveButtonBusy = true;
      });
      const data = {
        ...state.data,
        sale_type_id: state.data.sale_type_id.map((i) => i.id),
        channel_id: state.data.channel_id.map((i) => i.id),
      };
      const res = await editSurcharges(id, data);
      if (res.success) {
        successMessage("Updated Successfully");
        navigate(`/surcharges`);
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };
  const getSurchargesDetailsById = async () => {
    try {
      setState((draft) => {
        draft.isBusy = true;
      });
      const res = await getSurchargesDetails(SurchargesId);
      if (res.data) {
        setState((draft) => {
          draft.data = {
            ...res.data,
            sale_type_id: res.data.sale_types,
            channel_id: res.data.channels,
          };
          draft.name = res.data.name;
          draft.isFormChanged = false;
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };
  useEffect(() => {
    if (SurchargesId) {
      getSurchargesDetailsById();
    }
  }, [SurchargesId]);
  const onChange = (e) => {
    const { value, name } = e.target;

    setState((draft) => {
      draft.data[name] = value;
      draft.isFormChanged = true;
    });
  };
  const onDeleteStoreSelect = (data) => {
    setState((draft) => {
      draft.data.sale_type_id = [
        ...state.data.sale_type_id.filter((value) => {
          return value !== data;
        }),
      ];
      draft.isFormChanged = true;
    });
  };

  const onDeleteChannelSelect = (data) => {
    setState((draft) => {
      draft.data.channel_id = [
        ...state.data.channel_id.filter((value) => {
          return value !== data;
        }),
      ];
      draft.isFormChanged = true;
    });
  };
  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    setState((draft) => {
      draft.filters[name] = value;
      draft.filters.isSearching = false;
    });
  };

  const handleFilter = ({ name, value }) => {
    setState((draft) => {
      draft.filters.filter[name] = value;
      draft.filters.isFilterApplied = true;
    });
  };

  const handleApplyFilter = () => {
    const applied = [];
    state.filters.filter.channel.forEach((c) =>
      applied.push({ type: "Sales Channel", label: c.label, value: c.value }),
    );
    state.filters.filter.saleType.forEach((t) =>
      applied.push({ type: "Sale Type", label: t.label, value: t.value }),
    );
    setState((draft) => {
      draft.filters.appliedFilters = applied;
      draft.filters.isFilterApplied = false;
    });
    filterStateDispatch({
      type: "SET_DATA",
      page: "surcharges",
      data: {
        filterApplied: {
          filter: state.filters.filter,
          appliedFilters: applied,
        },
      },
    });
  };

  const handleFilterClear = (type, chip) => {
    if (type === "all") {
      setState((draft) => {
        draft.filters.filter.channel = [];
        draft.filters.filter.saleType = [];
        draft.filters.appliedFilters = [];
        draft.filters.isFilterApplied = false;
      });
      filterStateDispatch({
        type: "SET_DATA",
        page: "surcharges",
        data: {
          filterApplied: {
            filter: { channel: [], saleType: [] },
            appliedFilters: [],
          },
        },
      });
    } else {
      const updatedFilter = {
        channel:
          chip.type === "Sales Channel"
            ? state.filters.filter.channel.filter((c) => c.value !== chip.value)
            : state.filters.filter.channel,
        saleType:
          chip.type === "Sale Type"
            ? state.filters.filter.saleType.filter(
                (c) => c.value !== chip.value,
              )
            : state.filters.filter.saleType,
      };
      setState((draft) => {
        draft.filters.isFilterApplied = true;
        draft.filters.filter = updatedFilter;
      });
    }
  };

  const filteredSurchargesList = useMemo(() => {
    const channelFilter = state.filters.appliedFilters.filter(
      (f) => f.type === "Sales Channel",
    );
    const saleTypeFilter = state.filters.appliedFilters.filter(
      (f) => f.type === "Sale Type",
    );
    let list = state.surchargesList;
    if (channelFilter.length > 0) {
      const selectedIds = channelFilter.map((c) => c.value);
      list = list.filter((item) =>
        item.channels?.some((ch) => selectedIds.includes(ch.id)),
      );
    }
    if (saleTypeFilter.length > 0) {
      const selectedIds = saleTypeFilter.map((t) => t.value);
      list = list.filter((item) =>
        item.sale_types?.some((st) => selectedIds.includes(st.id)),
      );
    }
    return list;
  }, [state.surchargesList, state.filters.appliedFilters]);

  const pendingFilterChips = useMemo(
    () => [
      ...state.filters.filter.channel.map((c) => ({
        type: "Sales Channel",
        label: c.label,
        value: c.value,
      })),
      ...state.filters.filter.saleType.map((t) => ({
        type: "Sale Type",
        label: t.label,
        value: t.value,
      })),
    ],
    [state.filters.filter.channel, state.filters.filter.saleType],
  );

  useEffect(() => {
    if (!state.initialLoad && !state.filters.isSearching) {
      getSurChargesList(debouncedText);
    }
  }, [debouncedText]);

  const handleModal = (data, id) => {
    setState((draft) => {
      draft.deleteModalOpen = data;
      draft.selectedID = id;
    });
  };

  const handleDeleteSurCharges = async () => {
    try {
      const res = await deleteSurChargesById(state.selectedID);
      if (res.success) {
        successMessage(res.message);
        setState((draft) => {
          draft.surchargesList = draft.surchargesList.filter(
            (item) => item.id !== state.selectedID,
          );
        });
        handleModal(false, null);
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };

  return {
    state,
    onSaveSurcharges,
    onChange,
    globalData,
    onDeleteStoreSelect,
    onUpdateSurcharges,
    isCreateVisible,
    isEditable,
    isDeletable,
    taxState,
    onChangeFilter,
    handleFilter,
    handleApplyFilter,
    handleFilterClear,
    filteredSurchargesList,
    pendingFilterChips,
    handleModal,
    handleDeleteSurCharges,
    onDeleteChannelSelect,
  };
};
